import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { UserFavoriteCardData } from '../components/user-favorites/user-favorite-data.model';
import { OfferCardData } from '../components/offers/offer-card-data.model';
import { LoaderComponent } from '@shopping-angular-components/loader';
import { DistributionPassengers, DistributionPassengersContentModalComponent } from '../components/offers/distribution-passengers-content-modal/distribution-passengers-content-modal.component';
import { ModalData } from '@shopping-angular-components/modal/lib/models/ModalData.model';
import { ModalService } from '@shopping-angular-components/modal';
import { ConfirmButtonsComponent } from '../components/confirm-buttons/confirm-buttons.component';
import { TranslateService } from '@ngx-translate/core';
import { DistributionPassengersService } from './distribution-passengers.service';
import { LandingModelService } from './landingModel.service';

declare var window: any;

export interface FlightAvailabilityData {
    searchType: string,
    routes: Route[],
    emissionAdultPriceCode: string,
    emissionAdultPriceAmount: number,
    alternativeId: string,
    adultsCount: number,
    infantsCount: number,
    childsCount: number,
}

export interface Route {
    segments: Segment[],
    departureAirportCode: string,
    arrivalAirportCode: string,
}

export interface Segment {
    departureDateTime: string,
    arrivalDateTime: string,
    carrierCode: string,
    flightNumber: number,
    marketingCabinType: string,
}

export interface SnakeCaseSegment {
    departure_datetime: string,
    arrival_datetime: string,
    carrier_code: string,
    flight_number: number,
    marketing_cabin_type: string,
}

@Injectable({
    providedIn: 'root'
})
export class FlightsService {
    private AVAILABILITY_ENDPOINT = '/shop/flights-busquets/api/v1/web/searchItinerary';

    constructor (@Inject(PLATFORM_ID) private platformId: string,
                private httpClient: HttpClient,
                private translateService: TranslateService,
                private distributionPassengersService: DistributionPassengersService,
                private landingModelService: LandingModelService,
                private modalService: ModalService) {}

    showDistributionPassengersModal(moduleId: any, moduleIndex: any, locale: string, favorite: UserFavoriteCardData, nextFlow: string) {
      const distributionData: DistributionPassengers = { adults: 1, minors: 0 };

      const modalData = this.buildModalData(moduleId, moduleIndex, distributionData, () => {
        if (!this.distributionPassengersService.validateDistributionData(distributionData)) {
          const {adults, children, infants} = this.distributionPassengersService.getDistributionParameters(distributionData);
          this.redirectToCheckoutOrSearch(locale, adults, children, infants, favorite, nextFlow, distributionData);
        }
      });

      this.modalService.showModal(modalData);
    }

    showDistributionPassengersModalForOffer(moduleId: any, moduleIndex: any, offer: OfferCardData, newTab: boolean = false) {
      const distributionData: DistributionPassengers = { adults: 1, minors: 0 };

      const modalData = this.buildModalData(moduleId, moduleIndex, distributionData, () => {
        if (!this.distributionPassengersService.validateDistributionData(distributionData)) {
          const { adults, children, infants } = this.distributionPassengersService.getDistributionParameters(distributionData);
          this.redirectOfferToSearch(adults, children, infants, offer, newTab, distributionData);
        }
      });

      this.modalService.showModal(modalData);
    }

    redirectOfferToSearch(adults: number, children: number, infants: number, offer: OfferCardData, newTab: boolean = false, distributionData?: DistributionPassengers) {
      newTab ? this.modalService.closeModal() : this.startRedirect();
      
      let url = this.replacePassengersInUrl(offer.link.url, adults, children, infants, distributionData);
      const currencyCode = Object.keys(offer.price)[0];
      url = this.appendSearchParams(url, currencyCode, offer.price[currencyCode].total_amount_not_formatted);

      window.open(url, newTab ? '_blank' : '_self');
    }

    addClickedPriceToURL(currencyCode: string, clickedPrice: number, url: string): string {
        const separator = url.includes("?") ? "&" : "?";
        return url + `${separator}clickedPrice=${currencyCode}_${clickedPrice}`;
    }

    redirectToCheckoutOrSearch(locale: string, adults: number, children: number, infants: number, favorite: UserFavoriteCardData, nextFlow: string, distributionData?: DistributionPassengers) {
        this.startRedirect();

        let fallbackUrl = this.replacePassengersInUrl(favorite.link.url, adults, children, infants, distributionData);

        if (nextFlow == "MODAL_SEARCH") {
          const currencyCode = Object.keys(favorite.price)[0];
          fallbackUrl = this.appendSearchParams(fallbackUrl, currencyCode, favorite.price[currencyCode].total_amount_not_formatted);

          window.open(fallbackUrl, '_self')

        } else {
          this.getFlightAvailability(locale, adults, children, infants, favorite)
            .subscribe((response: any) => {
                const hasRedirectUrl = (response.nextStep && response.nextStep.url)
                if (hasRedirectUrl) {
                    window.open(response.nextStep.url, '_self')
                } else {
                    window.open(fallbackUrl, '_self')
                }
                LoaderComponent.showLoaderEvent.emit(false);
            },
            (err: any) => {
                // log error ?
                console.log(`Service error: ${JSON.stringify(err)}`)
                LoaderComponent.showLoaderEvent.emit(false);
                window.open(fallbackUrl, '_self')
            })
        }
    }

    getFlightAvailability<T>(locale: string, adults: number, children: number, infants: number, favorite: UserFavoriteCardData): Observable<T> {
        const url = `${this.AVAILABILITY_ENDPOINT}`;
        const headers: HttpHeaders = new HttpHeaders({
            ...this.getDefaultHeaders()
        });

        const data = this.buildFlightAvailabilityPayload(adults, children, infants, favorite);

        const firstRoute = data.routes[0];
        const lastRoute = data.routes[data.routes.length-1];
        const lastRouteFirstSegment = lastRoute.segments[0];

        let channel, site
        if (isPlatformBrowser(this.platformId) && window.upaData) {
            channel = window.upaData.ch
            site = window.upaData.cc
        }

        const origin = favorite.origin;
        const destination = favorite.destination;
        const departureDatetime = new Date(firstRoute.segments[0].departureDateTime);
        const departureDate = departureDatetime.toISOString().split('T')[0];
        const currencyCode = Object.keys(favorite.price)[0];
        let query = `?urlType=NYMERIA_LANDING&flow=LANDING&searchType=${data.searchType}&channel=${channel}&site=${site}&language=${locale}&adults=${adults}&children=${children}&infants=${infants}&from=${origin}&to=${destination}&limit=10&departureDate=${departureDate}&currency=${currencyCode}`;

        if (data.searchType == 'ROUND_TRIP') {
          const returnDatetime = new Date(lastRouteFirstSegment.departureDateTime);
          const returnDate = returnDatetime.toISOString().split('T')[0];
          query += `&returnDate=${returnDate}`;
        }

        return this.httpClient.post<T>(url+query, data, {
            observe: 'body',
            responseType: 'json',
            withCredentials: true,
            headers: headers,
        });
    }

    private getDefaultHeaders(): { [key: string]: string } {
        if (isPlatformBrowser(this.platformId)) {
            return {
                'x-client': 'shopping',
                'domain': window.location.hostname,
            };
        }
        return {}
    }

    private buildFlightAvailabilityPayload(adults: number, children: number, infants: number, favorite: UserFavoriteCardData): FlightAvailabilityData {
        const currencyCode = Object.keys(favorite.price)[0];
        const priceAmount = favorite.price[currencyCode].total_amount_not_formatted;
        return {
          adultsCount: adults,
          childsCount: children,
          infantsCount: infants,
          alternativeId: favorite.external_id,
          emissionAdultPriceAmount: priceAmount,
          emissionAdultPriceCode: currencyCode,
          searchType: favorite.itineraries.length > 1 ? 'ROUND_TRIP' : 'ONE_WAY',
          routes: favorite.itineraries.map(it => {
            return {
              segments: it.segments.map(s => {
                return {
                  arrivalDateTime: s.arrival_datetime,
                  departureDateTime: s.departure_datetime,
                  carrierCode: s.carrier_code,
                  flightNumber: s.flight_number,
                  marketingCabinType: s.marketing_cabin_type
                }
              }),
              departureAirportCode: it.from,
              arrivalAirportCode: it.to
            }
          }),
        }
    }

    private buildModalData(moduleId: any, moduleIndex: any, distributionData: DistributionPassengers, acceptCallback: () => void): ModalData {
      return {
        id: 'distribution-passengers-modal',
        title: this.translateService.instant("distributionPassengers.modal.title"),
        size: '-sm',
        contentComponent: DistributionPassengersContentModalComponent,
        contentModel: {
          distributionData: distributionData
        },
        footerComponent: ConfirmButtonsComponent,
        footerModel: {
          acceptCallback,
          acceptLabel: this.translateService.instant("distributionPassengers.modal.cta"),
          isCancelable: false,
          moduleId: moduleId,
          moduleIndex: moduleIndex,
          // uniqueId: this.uniqueId,
          size: globalThis.innerWidth <= 340 ? '-md' : '-lg',
          // upaData: {...this.upaData, data: item}
        },
        isWebview: this.landingModelService.getLandingModel().webview,
        product: 'nymeria-gui',
        flow: 'landing',
        forceHeader: true
      };
    }

    private startRedirect() {
      LoaderComponent.showLoaderEvent.emit(true);
      this.modalService.closeModal();
    }

    private replacePassengersInUrl(url: string, adults: number, children: number, infants: number, distributionData?: DistributionPassengers): string {
      if (url.includes("{adults}") || url.includes("{children}") || url.includes("{infants}")) {
        url = url
          .replace("{adults}", adults.toString())
          .replace("{children}", children.toString())
          .replace("{infants}", infants.toString());
      } else {
        url = url.replace(/(?:\/\d+\/\d+\/\d+)(\?|$)/, `/${adults}/${children}/${infants}$1`);
      }

      if (distributionData) {
        const diValue = this.distributionPassengersService.buildDiParam(distributionData);
        const diRegex = /([?&])di=[^&]*/;
        if (diRegex.test(url)) {
          url = url.replace(diRegex, `$1di=${diValue}`);
        } else {
          const separator = url.includes('?') ? '&' : '?';
          url += `${separator}di=${diValue}`;
        }
      }

      return url;
    }

    private appendSearchParams(url: string, currencyCode: string, clickedPrice: number): string {
      if (!url.includes("clickedPrice=")) {
        url = this.addClickedPriceToURL(currencyCode, clickedPrice, url);
      }
      if (!url.includes("currency=")) {
        url += `&currency=${currencyCode}`;
      }
      return url;
    }
}